import {
  BolAddressBlock,
  BolDocument,
  BolFieldValue,
  BolItem,
  fieldHasDisplayValue,
} from "../../../../app/data/pickup-requests/bolModels";

export interface BolFormIssue {
  label: string;
  severity: "error" | "warning" | "success";
}

const checkField = (label: string, field?: BolFieldValue | null): BolFormIssue | null => {
  if (!field) return null;
  if (!fieldHasDisplayValue(field)) {
    return { label: `${label} not detected`, severity: field.confidence < 0.6 ? "error" : "warning" };
  }
  if (field.confidence < 0.85 || field.aiMessage) {
    return { label: `${label} needs verification`, severity: "warning" };
  }
  return null;
};

const checkAddress = (prefix: string, block?: BolAddressBlock): BolFormIssue[] => {
  if (!block) return [];
  const fields: Array<[string, BolFieldValue | undefined]> = [
    ["Company", block.name],
    ["Phone", block.phoneNumber],
    ["Address", block.addressLine_1],
    ["City", block.city],
    ["ZIP", block.zip],
  ];
  return fields.map(([l, f]) => checkField(`${prefix} ${l}`, f)).filter(Boolean) as BolFormIssue[];
};

export const collectBolIssues = (doc: BolDocument): BolFormIssue[] => {
  const issues: BolFormIssue[] = [];

  [
    ["Pickup date", doc.pickupWindow?.windowDate],
    ["Start time", doc.pickupWindow?.windowStartTime],
    ["End time", doc.pickupWindow?.windowEndTime],
    ["BOL", doc.bol],
    ["PO number", doc.purchaseOrder],
    ["Additional reference", doc.orderDescription],
  ].forEach(([label, field]) => {
    const issue = checkField(label, field);
    if (issue) issues.push(issue);
  });

  issues.push(...checkAddress("Shipper", doc.pickupFrom));
  issues.push(...checkAddress("Consignee", doc.shipTo));

  (doc.items || []).forEach((item: BolItem, idx: number) => {
    const classIssue = checkField(`Class code (item ${idx + 1})`, item.classCode);
    if (classIssue) issues.push(classIssue);
  });

  return issues;
};

export const countAutoFilledFields = (doc: BolDocument): number => {
  let count = 0;
  const visit = (f?: BolFieldValue | null) => {
    if (fieldHasDisplayValue(f)) count += 1;
  };
  visit(doc.bol);
  visit(doc.purchaseOrder);
  visit(doc.totalWeight);
  Object.values(doc.pickupFrom || {}).forEach(visit);
  Object.values(doc.shipTo || {}).forEach(visit);
  Object.values(doc.pickupWindow || {}).forEach(visit);
  (doc.items || []).forEach((item) => Object.values(item).forEach(visit));
  return count;
};

export const averageBolConfidence = (doc: BolDocument): number | null => {
  const scores: number[] = [];
  const visit = (f?: BolFieldValue | null) => {
    if (f && typeof f.confidence === "number") scores.push(f.confidence);
  };
  visit(doc.bol);
  Object.values(doc.pickupFrom || {}).forEach(visit);
  Object.values(doc.shipTo || {}).forEach(visit);
  Object.values(doc.pickupWindow || {}).forEach(visit);
  (doc.items || []).forEach((item) => Object.values(item).forEach(visit));
  if (!scores.length) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
};
